import { Stack } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '@/shared/components/ui/avatar/avatar';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import {
  NameCellText,
  NameContainer,
} from '@/shared/components/ui/data-table/data-table.styled';
import { OverflowText } from '@/shared/components/ui/overflow-text/overflow-text';
import { SearchableResourceField } from '@/shared/components/ui/searchable-resource-field/searchable-resource-field';
import { SecretToggle } from '@/shared/components/ui/secret-toggle/secret-toggle';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';

export const createNameRenderer = (
  IconComponent?: React.ComponentType,
  iconProps: any = {},
  paramName: string = 'name',
) => {
  return ({ data }: ICellRendererParams) => {
    const { getItemLink, disableItemClick } = useDataTable();
    const navigate = useNavigate();
    const link = getItemLink?.(data) ?? '';

    const isClickable = Boolean(link) && !disableItemClick?.(data);

    const handleClick = () => {
      if (!isClickable) {
        return;
      }
      navigate(link);
    };

    return (
      <NameContainer
        $clickable={isClickable}
        onClick={handleClick}
        height="100%"
      >
        {IconComponent && (
          <Stack height="100%" justifyContent="center">
            <IconComponent variant="primary" {...iconProps} />
          </Stack>
        )}
        <NameCellText variant="p7">
          <SearchableResourceField
            value={data[paramName]}
            queryParam={paramName}
          />
        </NameCellText>
      </NameContainer>
    );
  };
};

export const DefaultCellRenderer: React.FC<ICellRendererParams> = ({
  value,
}) => {
  return (
    <NameContainer height="100%">
      <NameCellText variant="p8">
        <OverflowText text={String(value ?? '-')} />
      </NameCellText>
    </NameContainer>
  );
};

export const TimestampRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  const { formatLocalTime } = useDateUtils();
  return (
    <NameContainer height="100%">
      <NameCellText variant="p8">
        <OverflowText text={formatLocalTime(value)} />
      </NameCellText>
    </NameContainer>
  );
};

export const CreatedByRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  const props = typeof value === 'string' ? { userId: value } : { user: value };
  return <Avatar {...props} />;
};

export const SecretsRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  return <SecretToggle secret={value} />;
};

export const CommentRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  return (
    <NameContainer height="100%">
      <NameCellText variant="p8">
        <OverflowText text={value ?? '-'} />
      </NameCellText>
    </NameContainer>
  );
};
